import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save, ArrowLeft, Calendar, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useTaxYear } from "@/context/TaxYearContext";
import { TaxYearSelect } from "@/components/TaxYearSelect";

const DEFAULT_INSTALLMENTS = [
  { label: "Installment 1", due_date: "2025-06-15", cumulative_pct: 15,  actual_paid: 0, paid_on: "" },
  { label: "Installment 2", due_date: "2025-09-15", cumulative_pct: 45,  actual_paid: 0, paid_on: "" },
  { label: "Installment 3", due_date: "2025-12-15", cumulative_pct: 75,  actual_paid: 0, paid_on: "" },
  { label: "Installment 4", due_date: "2026-03-15", cumulative_pct: 100, actual_paid: 0, paid_on: "" },
];

const PRESUMPTIVE_INSTALLMENTS = [
  { label: "Presumptive (100% by 15-Mar)", due_date: "2026-03-15", cumulative_pct: 100, actual_paid: 0, paid_on: "" },
];

function Inner() {
  const { activeId } = useCompany();
  const { ay } = useTaxYear();
  const navigate = useNavigate();
  const [plan, setPlan] = useState({
    ay: "2026-27",
    estimated_liability: 0,
    installments: DEFAULT_INSTALLMENTS,
    is_presumptive: false,
    notes: "",
  });
  const [computed, setComputed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const preview = useCallback(async (p) => {
    if (!activeId) return;
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/advance-tax/preview`, p);
      setComputed(data);
    } catch { /* silent */ }
  }, [activeId]);

  useEffect(() => {
    if (!activeId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/companies/${activeId}/tax/advance-tax?ay=${ay}`);
        if (data) {
          const inst = (data.installments && data.installments.length > 0)
            ? data.installments
            : (data.is_presumptive ? PRESUMPTIVE_INSTALLMENTS : DEFAULT_INSTALLMENTS);
          setPlan({ ...data, ay, installments: inst });
          setComputed(data.computed || null);
        } else {
          setPlan((p) => ({ ...p, ay }));
        }
      } catch (err) {
        toast.error(describeAxiosError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [activeId, ay]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => preview(plan), 400);
    return () => clearTimeout(t);
  }, [plan, loading, preview]);

  const updateInstallment = (idx, patch) => {
    setPlan((p) => {
      const arr = [...p.installments];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...p, installments: arr };
    });
  };

  const togglePresumptive = (v) => {
    setPlan((p) => ({
      ...p,
      is_presumptive: v,
      installments: v ? PRESUMPTIVE_INSTALLMENTS : DEFAULT_INSTALLMENTS,
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/companies/${activeId}/tax/advance-tax`, { ...plan, ay });
      toast.success("Advance Tax Plan saved");
    } catch (err) {
      toast.error(describeAxiosError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  return (
    <>
      <PageHeader
        title={`Advance-Tax Planner — AY ${ay}`}
        subtitle="Installment schedule per Section 211(1). Auto-computes 234C / 234B interest on shortfalls."
        actions={
          <>
            <TaxYearSelect />
            <Button variant="outline" onClick={() => navigate("/tax")} className="h-8" data-testid="btn-adv-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button onClick={save} disabled={saving} className="h-8 bg-blue-700 hover:bg-blue-800" data-testid="btn-adv-save">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save Plan"}
            </Button>
          </>
        }
      />
      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Header inputs */}
          <div className="border border-gray-200 bg-white rounded p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[11px] uppercase text-gray-600">Estimated Tax Liability (₹)</label>
                <Input
                  type="number"
                  value={plan.estimated_liability}
                  onChange={(e) => setPlan({ ...plan, estimated_liability: Number(e.target.value) })}
                  className="h-9 text-right font-mono"
                  data-testid="input-estimated-liability"
                />
                <p className="text-[10px] text-gray-500">Includes surcharge + cess, before TDS</p>
              </div>
              <label className="flex items-start gap-2 pt-4">
                <input
                  type="checkbox"
                  checked={plan.is_presumptive}
                  onChange={(e) => togglePresumptive(e.target.checked)}
                  className="mt-0.5"
                  data-testid="check-is-presumptive"
                />
                <div>
                  <div className="text-sm font-medium">Presumptive taxpayer (44AD/44ADA)</div>
                  <div className="text-[11px] text-gray-500">Pays 100% of advance tax in a single installment by 15-Mar.</div>
                </div>
              </label>
            </div>
          </div>

          {/* Installments table */}
          <div className="border border-gray-200 bg-white rounded overflow-x-auto" data-testid="adv-installments-table">
            <table className="tx-table w-full">
              <thead>
                <tr>
                  <th>Installment</th>
                  <th>Due Date</th>
                  <th className="text-right">Cum %</th>
                  <th className="text-right">Expected Cum</th>
                  <th className="text-right">Actual Paid</th>
                  <th>Paid On</th>
                  <th className="text-right">Shortfall</th>
                  <th className="text-right">234C</th>
                </tr>
              </thead>
              <tbody>
                {plan.installments.map((inst, idx) => {
                  const cinst = computed?.installments?.[idx] || {};
                  const overdue = cinst.shortfall > 0;
                  return (
                    <tr key={idx} className={overdue ? "bg-red-50/40" : ""}>
                      <td className="text-xs font-medium">{inst.label}</td>
                      <td className="text-xs font-mono">
                        <Input type="date" value={inst.due_date} onChange={(e) => updateInstallment(idx, { due_date: e.target.value })} className="h-8 text-xs" />
                      </td>
                      <td className="text-right font-mono text-xs">{inst.cumulative_pct}%</td>
                      <td className="text-right font-mono text-xs">₹ {formatINR(cinst.expected_cumulative || 0)}</td>
                      <td>
                        <Input
                          type="number"
                          value={inst.actual_paid}
                          onChange={(e) => updateInstallment(idx, { actual_paid: Number(e.target.value) })}
                          className="h-8 text-right font-mono text-xs"
                          data-testid={`input-adv-paid-${idx}`}
                        />
                      </td>
                      <td>
                        <Input type="date" value={inst.paid_on || ""} onChange={(e) => updateInstallment(idx, { paid_on: e.target.value })} className="h-8 text-xs" />
                      </td>
                      <td className="text-right font-mono text-xs">
                        {cinst.shortfall > 0 ? (
                          <span className="text-red-700 font-semibold">₹ {formatINR(cinst.shortfall)}</span>
                        ) : (
                          <span className="text-emerald-700">—</span>
                        )}
                      </td>
                      <td className="text-right font-mono text-xs">
                        {cinst.interest_234c > 0 ? (
                          <span className="text-red-700 font-semibold" data-testid={`adv-234c-${idx}`}>₹ {formatINR(cinst.interest_234c)}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td colSpan={4} className="text-right text-xs">Total Paid / 234C</td>
                  <td className="text-right font-mono text-xs" data-testid="adv-total-paid">₹ {formatINR(computed?.total_paid || 0)}</td>
                  <td colSpan={2}></td>
                  <td className="text-right font-mono text-xs text-red-700" data-testid="adv-234c-total">₹ {formatINR(computed?.interest_234c_total || 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="border border-gray-200 bg-white rounded p-4">
            <label className="text-[11px] uppercase text-gray-600 block mb-1">Notes</label>
            <textarea value={plan.notes} onChange={(e) => setPlan({ ...plan, notes: e.target.value })} className="w-full text-xs p-2 border border-gray-200 rounded" rows={3} placeholder="Optional working notes" />
          </div>
        </div>

        {/* RIGHT: Summary */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-16 space-y-3">
            <div className="border border-gray-200 bg-white rounded" data-testid="adv-summary-card">
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="font-semibold text-sm">Summary</h3>
              </div>
              <div className="p-4 space-y-3 text-xs">
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                  <div className="text-gray-600">Estimated liability</div>
                  <div className="text-right font-mono font-medium">₹ {formatINR(computed?.estimated_liability || 0)}</div>
                  <div className="text-gray-600">Total paid</div>
                  <div className="text-right font-mono">₹ {formatINR(computed?.total_paid || 0)}</div>
                  <div className="text-gray-600">Total shortfall</div>
                  <div className="text-right font-mono text-red-700">₹ {formatINR(computed?.total_shortfall || 0)}</div>
                </div>
                <div className="border-t border-gray-100 pt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
                  <div className="text-gray-700 font-medium">234C Interest</div>
                  <div className="text-right font-mono font-medium text-red-700" data-testid="summary-234c">₹ {formatINR(computed?.interest_234c_total || 0)}</div>
                  <div className="text-gray-700 font-medium">234B Estimate</div>
                  <div className="text-right font-mono font-medium text-red-700" data-testid="summary-234b">₹ {formatINR(computed?.interest_234b_estimate || 0)}</div>
                </div>
                <div className="bg-blue-50 -mx-4 -mb-4 px-4 py-3 border-t border-blue-200">
                  <div className="flex justify-between font-semibold text-sm">
                    <span>Total with Interest</span>
                    <span className="font-mono" data-testid="summary-grand-total">₹ {formatINR(computed?.grand_total_with_interest || 0)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Compliance status */}
            <div className="border border-gray-200 bg-white rounded p-4 text-xs">
              <div className="text-[11px] uppercase tracking-wider text-gray-600 font-semibold mb-2">Compliance Status</div>
              {(computed?.installments || []).map((cinst, idx) => (
                <div key={idx} className="flex items-center gap-2 py-1">
                  {cinst.shortfall > 0 ? (
                    <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                  ) : (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  )}
                  <span className="text-xs">
                    {plan.installments[idx]?.label} — {cinst.shortfall > 0 ? `₹${formatINR(cinst.shortfall)} short` : "On time"}
                  </span>
                </div>
              ))}
            </div>

            <div className="border border-blue-200 bg-blue-50/40 rounded p-3 text-[11px] text-blue-900 leading-relaxed">
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Section 211(1)</strong> installments: 15% by 15-Jun, 45% by 15-Sep, 75% by 15-Dec, 100% by 15-Mar. Presumptive taxpayers pay 100% in a single installment by 15-Mar.
                  <br />
                  <strong>234C</strong>: 1%/mo × 3 months (installments 1-3) or 1 month (installment 4) on shortfall from cumulative expected.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function AdvanceTaxPlanner() {
  return <RequireCompany><Inner /></RequireCompany>;
}
